import { isNaN } from 'formik'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import { EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiLoadingContent, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { apiDownloadMasterCampaignPlanResource } from 'api/adcritterApi'
import { MasterCampaignPlanResourceType, MasterCampaignPlanStatus } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder } from 'api/interfaces/MasterCampaignStub'
import { useCreateMasterCampaignPlanPublicIdMutation, useGetMasterCampaignPlanMasterCampaignStubQuery, useGetMasterCampaignPlanQuery, useMasterCampaignPlanResetAllMutation, useMasterCampaignStubResetMutation, useRemoveMasterCampaignStubCampaignBuilderMutation, useUpdateInEditModeMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { useTransferMasterCampaignPlanToDspMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import config from 'app/config'
import { RootState } from 'app/rootReducer'
import AdCritterAiResearchDocuments from 'components/AdCritterAiResearchDocuments'
import GanttChartOld from 'features/planner/GanttChartOld'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'
import { getMasterCampaignStubAverageMonthlyBudgetOld } from 'utils/CampaignMathUtils'
import { calculateEndDate } from 'utils/MasterCampaignPlanUtils'

import { EditType } from '../../components/EditFlyout'
import { EditFlyout } from '../../components/EditFlyout'
import EditCampaignFlyoutOld, { EditCampaignFlyoutModeOld } from '../EditCampaignFlyoutOld'
import { CampaignCardOld } from '../components/CampaignCardOld'
import { ConfirmTotalResetModal } from './components/ConfirmTotalResetModal'
import { InEditModeCalloutOld } from './components/InEditModeCalloutOld'
import { SummarySection } from './components/SummarySection'

interface IEditCampaignFlyoutOpen {
  mode: EditCampaignFlyoutModeOld
  selectedCampaignBuilderId?: string
}

interface MasterCampaignPlanPageParams {
  masterCampaignPlanId: string
}

const SLICE_CAMPAIGNS_COUNT = 3

export const MasterCampaignPlanPageOld = () => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  })

  const history = useHistory()
  const { masterCampaignPlanId } = useParams<MasterCampaignPlanPageParams>()
  const { currentOrganization, currentAccount, isStaffUser } = useSelector((state: RootState) => state.app)
  const { data: masterCampaignPlan, isFetching: isMasterCampaignPlanFetching } = useGetMasterCampaignPlanQuery({ masterCampaignPlanId: masterCampaignPlanId })
  const { data: masterCampaignStub, isFetching: isMasterCampaignStubFetching } = useGetMasterCampaignPlanMasterCampaignStubQuery({ masterCampaignPlanId: masterCampaignPlanId })
  const [updateInEditMode, { isLoading: isUpdatingInEditMode }] = useUpdateInEditModeMutation()
  const [transferMasterCampaignPlanToDspMutation, transferMasterCampaignPlanToDspResult] = useTransferMasterCampaignPlanToDspMutation()
  const [resetMasterCampaignPlanMutation, { isLoading: isResettingMasterCampaignPlan }] = useMasterCampaignPlanResetAllMutation()
  const [resetMasterCampaignStubMutation, { isLoading: isResettingMasterCampaignStub }] = useMasterCampaignStubResetMutation()
  const [removeMasterCampaignStubCampaignBuilderMutation, removeMasterCampaignStubCampaignBuilderResult] = useRemoveMasterCampaignStubCampaignBuilderMutation()
  const [createMasterCampaignPlanPublicId, { isLoading: isCreatingPublicId }] = useCreateMasterCampaignPlanPublicIdMutation()

  const [viewAll, setViewAll] = useState(false)
  const [transferToDspModalVisible, setTransferToDspModalVisible] = useState(false)

  const [inEditMode, setInEditMode] = useState(masterCampaignPlan?.inEditMode ?? false)
  const [editFlyoutOpen, setEditFlyoutOpen] = useState(false)
  const [editCampaignFlyoutOpen, setEditCampaignFlyoutOpen] = useState<IEditCampaignFlyoutOpen | null>(null)
  const [editFlyoutType, setEditFlyoutType] = useState<EditType | null>(null)
  const [confirmTotalResetModalVisible, setConfirmTotalResetModalVisible] = useState(false)

  const isNewMasterCampaignPlan = masterCampaignPlanId !== masterCampaignPlan?.id

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const editParam = urlParams.get('edit')
    if (editParam === 'true') {
      updateInEditMode({ masterCampaignPlanId: masterCampaignPlanId, inEditMode: true })
    }
  }, [masterCampaignPlanId, updateInEditMode])

  useEffect(() => {
    if (masterCampaignPlan && !isStaffUser && (masterCampaignPlan.status === MasterCampaignPlanStatus.Migrating || masterCampaignPlan.status === MasterCampaignPlanStatus.Processing)) {
      history.push('/ai')
    }
  }, [masterCampaignPlan, history, isStaffUser])

  const onEditFlyoutClose = () => {
    setEditFlyoutOpen(false)
    setEditFlyoutType(null)
  }

  const onEditCampaignFlyoutClose = () => {
    setEditCampaignFlyoutOpen(null)
  }

  const onEditClick = async () => {
    const result = await updateInEditMode({ masterCampaignPlanId: masterCampaignPlanId, inEditMode: !inEditMode }).unwrap()
    setInEditMode(result.inEditMode)
  }

  const onEditSectionClick = (editType: EditType) => {
    setEditFlyoutOpen(true)
    setEditFlyoutType(editType)
  }

  const onEditCampaignClick = (builder: MasterCampaignPlanCampaignBuilder) => {
    setEditCampaignFlyoutOpen({ mode: EditCampaignFlyoutModeOld.Edit, selectedCampaignBuilderId: builder.id })
  }

  const onRemoveCampaignClick = async (builder: MasterCampaignPlanCampaignBuilder) => {
    if (!masterCampaignPlan?.id || !masterCampaignStub?.id || !builder.id) {
      return
    }

    await removeMasterCampaignStubCampaignBuilderMutation({ masterCampaignPlanId: masterCampaignPlan.id, masterCampaignStubId: masterCampaignStub.id, masterCampaignStubCampaignBuilderId: builder.id })
  }

  useEffect(() => {
    if (!currentAccount || !masterCampaignPlan) {
      return
    }

    setInEditMode(masterCampaignPlan.inEditMode)

    if (currentAccount?.id !== masterCampaignPlan?.accountId) {
      history.push('/ai')
    }
  }, [currentAccount, masterCampaignPlan, history])

  useEffect(() => {
    if (!transferMasterCampaignPlanToDspResult.isSuccess || !transferMasterCampaignPlanToDspResult.data) {
      return
    }

    setTransferToDspModalVisible(false)
    history.push(`/masterCampaigns/${transferMasterCampaignPlanToDspResult.data.id}`)
  }, [transferMasterCampaignPlanToDspResult, history])

  const onTakeMeToDspClick = () => {
    if (!currentOrganization?.id || !currentAccount?.id || !masterCampaignPlan?.id) {
      return
    }

    transferMasterCampaignPlanToDspMutation({
      organizationId: currentOrganization.id,
      accountId: currentAccount.id,
      masterCampaignPlanId: masterCampaignPlan.id
    })
  }

  const onCampaignPreviewClick = async () => {
    if (!masterCampaignPlan?.id) {
      return
    }

    let publicId = masterCampaignPlan.publicId

    if (!publicId) {
      const updatedPlan = await createMasterCampaignPlanPublicId({
        masterCampaignPlanId: masterCampaignPlan.id
      }).unwrap()

      publicId = updatedPlan.publicId
    }

    if (publicId) {
      window.open(`${config.campaignPreviewUrl}${publicId}`, '_blank')
    }
  }

  const averageMonthlyBudget = masterCampaignPlan && masterCampaignStub ? getMasterCampaignStubAverageMonthlyBudgetOld(masterCampaignPlan, masterCampaignStub) : 0

  const listCampaigns = [...(masterCampaignStub?.campaignBuilders ?? [])]
    .sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
    .slice(0, viewAll && masterCampaignStub?.campaignBuilders ? masterCampaignStub?.campaignBuilders.length : SLICE_CAMPAIGNS_COUNT)
    .map(builder => {
      const calculatedEndDate = calculateEndDate(builder)
      return (
        <>
          <CampaignCardOld
            builder={{ ...builder, endDate: calculatedEndDate }}
            key={builder.id}
            inEditMode={inEditMode}
            onEditClick={() => {
              onEditCampaignClick(builder)
            }}
            onRemoveCampaignLoading={removeMasterCampaignStubCampaignBuilderResult.isLoading}
            onRemoveCampaignClick={() => onRemoveCampaignClick(builder)}
          />
          <EuiSpacer size={'m'} />
        </>
      )
    })

  const downloadPdf = async ({ masterCampaignPlanResourceType, fileName }: { masterCampaignPlanResourceType: MasterCampaignPlanResourceType; fileName: string }) => {
    if (!masterCampaignPlan || !masterCampaignPlanResourceType) {
      return
    }
    await apiDownloadMasterCampaignPlanResource(masterCampaignPlanId, masterCampaignPlanResourceType).then(data => {
      const blobUrl = window.URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', `${masterCampaignPlan.name}_${fileName}`)
      link.click()
      link.remove()
      URL.revokeObjectURL(blobUrl)
    })
  }

  if ((isNewMasterCampaignPlan && (isMasterCampaignPlanFetching || isMasterCampaignStubFetching)) || !masterCampaignPlan || !masterCampaignStub) {
    return <EuiLoadingContent lines={3} />
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection style={{ width: '100%' }}>
            <EuiFlexGroup>
              <EuiFlexItem grow={true}>
                <EuiTitle>
                  <h1>Client: {currentAccount?.name}</h1>
                </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton fill size={'s'} onClick={() => history.push('/ai/create-plan')}>
                  Create New Master Campaign Plan
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false}>
          <div style={{ padding: '24px' }} className='eui-xScroll'>
            {inEditMode && (
              <>
                <InEditModeCalloutOld onExitEditModeClick={onEditClick} onResetClick={() => setConfirmTotalResetModalVisible(true)} />
                <EuiSpacer />
              </>
            )}
            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiFlexGroup alignItems='center' justifyContent='flexStart'>
                  <EuiTitle>
                    <h2>{masterCampaignPlan?.name}</h2>
                  </EuiTitle>
                  {inEditMode && <EuiButtonIcon style={{ marginLeft: '12px' }} iconType='pencil' display='base' onClick={() => onEditSectionClick(EditType.Name)} />}
                </EuiFlexGroup>
              </EuiFlexItem>
              {!inEditMode && (
                <>
                  {masterCampaignPlan?.status === MasterCampaignPlanStatus.TransferredToDsp ? (
                    <EuiFlexItem grow={false}>
                      <EuiButton fill size={'s'} iconType={'popout'} onClick={() => history.push(`/masterCampaigns/${masterCampaignPlan?.masterCampaignId}`)}>
                        View in DSP
                      </EuiButton>
                    </EuiFlexItem>
                  ) : (
                    <EuiFlexItem grow={false}>
                      <EuiButton size='s' onClick={onEditClick} isLoading={isUpdatingInEditMode} iconType={'pencil'}>
                        Edit Plan
                      </EuiButton>
                    </EuiFlexItem>
                  )}
                </>
              )}
              {masterCampaignPlan?.status !== MasterCampaignPlanStatus.TransferredToDsp && !inEditMode && (
                <EuiFlexItem grow={false}>
                  <EuiButton fill size={'s'} iconType={'eye'} onClick={onCampaignPreviewClick} isLoading={isCreatingPublicId}>
                    Client Preview
                  </EuiButton>
                </EuiFlexItem>
              )}
              {masterCampaignPlan?.status === MasterCampaignPlanStatus.Ready && !inEditMode && (
                <EuiFlexItem grow={false}>
                  <EuiButton fill size={'s'} iconType={'popout'} onClick={() => setTransferToDspModalVisible(true)}>
                    Transfer to DSP
                  </EuiButton>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
            <EuiSpacer />
            <EuiFlexGroup gutterSize={'xl'} justifyContent='flexStart'>
              <EuiFlexItem>
                <GanttChartOld
                  chartItems={
                    masterCampaignStub?.campaignBuilders.map(c => {
                      return {
                        name: c.name,
                        type: c.campaignType,
                        startDate: moment(c.startDate),
                        endDate: calculateEndDate(c)
                      }
                    }) ?? []
                  }
                />
                <EuiSpacer size={'xxl'} />
                <SummarySection title='Plan Summary' content={masterCampaignPlan?.planSummary} inEditMode={inEditMode} onEditClick={() => onEditSectionClick(EditType.Plan)} />
                <SummarySection title='Geo-Targeting' content={masterCampaignPlan?.geoSummary} inEditMode={inEditMode} onEditClick={() => onEditSectionClick(EditType.Geo)} />
                <SummarySection title='Audience Targeting' content={masterCampaignPlan?.audienceSummary} inEditMode={inEditMode} onEditClick={() => onEditSectionClick(EditType.Audience)} />
                <SummarySection title='Seasonality' content={masterCampaignPlan?.seasonalitySummary} inEditMode={inEditMode} onEditClick={() => onEditSectionClick(EditType.Seasonality)} />
                <AdCritterAiResearchDocuments masterCampaignPlanResources={masterCampaignPlan.resources} downloadPdf={(type: MasterCampaignPlanResourceType, fileName: string) => downloadPdf({ masterCampaignPlanResourceType: type, fileName })} />
              </EuiFlexItem>
              <EuiFlexItem grow={false} />
              <EuiFlexItem>
                <EuiTitle size={'s'}>
                  <p>AI Generated Campaigns</p>
                </EuiTitle>
                <EuiSpacer size='m' />
                <EuiText>
                  Based on a targeted monthly budget, AdCritter AI has created the {masterCampaignStub?.campaignBuilders.length} campaigns below with an average monthly spend of {formatter.format(isNaN(averageMonthlyBudget) ? 0 : averageMonthlyBudget)}. Each campaign has specific geographic targeting and audience segments based on the marketing strategy.
                </EuiText>

                <EuiSpacer size={'m'} />

                <EuiText>You can click Transfer to DSP and the campaigns and full targeting will be available in AdCritter DSP where you can modify and activate them as needed.</EuiText>

                <EuiHorizontalRule />

                {inEditMode && (
                  <>
                    <EuiFlexGroup justifyContent='flexEnd'>
                      <EuiFlexItem grow={false}>
                        <EuiButton fill size={'s'} onClick={() => setEditCampaignFlyoutOpen({ mode: EditCampaignFlyoutModeOld.Add })}>
                          Add Campaign
                        </EuiButton>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                    <EuiSpacer size={'m'} />
                  </>
                )}

                <EuiFlexGroup>
                  <EuiFlexItem>{listCampaigns}</EuiFlexItem>
                </EuiFlexGroup>

                {(masterCampaignStub?.campaignBuilders.length ?? 0) > SLICE_CAMPAIGNS_COUNT && (
                  <EuiFlexGroup>
                    <EuiFlexItem />
                    <EuiFlexItem grow={false}>
                      <EuiButton fill onClick={() => setViewAll(!viewAll)}>
                        {viewAll ? 'View Fewer Campaigns' : `View All ${masterCampaignStub?.campaignBuilders.length} Campaigns`}
                      </EuiButton>
                    </EuiFlexItem>
                    <EuiFlexItem />
                  </EuiFlexGroup>
                )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </div>
        </EuiPanel>
      </EuiPageBody>
      {transferToDspModalVisible && (
        <AgencyV2ModalOld isLoading={transferMasterCampaignPlanToDspResult.isLoading} title='Transfer to DSP' onClose={() => setTransferToDspModalVisible(false)} showCancelButton confirmButtonText='Transfer' onConfirmButtonClick={onTakeMeToDspClick} bodyHeight='11em'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>This will copy all of the campaigns and documents from this plan into AdCritter DSP where you will have full editing control.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>Are you ready to transfer this plan into the DSP?</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </AgencyV2ModalOld>
      )}
      {editFlyoutOpen && editFlyoutType && <EditFlyout editType={editFlyoutType} onClose={onEditFlyoutClose} />}
      {editCampaignFlyoutOpen && <EditCampaignFlyoutOld editMode={editCampaignFlyoutOpen.mode} selectedCampaignBuilderId={editCampaignFlyoutOpen.selectedCampaignBuilderId} onClose={onEditCampaignFlyoutClose} />}
      {confirmTotalResetModalVisible && (
        <ConfirmTotalResetModal
          onClose={() => setConfirmTotalResetModalVisible(false)}
          isLoading={isResettingMasterCampaignPlan || isResettingMasterCampaignStub}
          onResetAll={async () => {
            if (!masterCampaignPlan?.id || !currentOrganization?.id || !currentAccount?.id || !masterCampaignStub?.id) {
              return
            }
            const resetPlanPromise = resetMasterCampaignPlanMutation({ organizationId: currentOrganization.id, accountId: currentAccount.id, masterCampaignPlanId: masterCampaignPlan.id })
            const resetStubPromise = resetMasterCampaignStubMutation({ masterCampaignPlanId: masterCampaignPlan.id, masterCampaignStubId: masterCampaignStub.id })

            await Promise.all([resetPlanPromise, resetStubPromise])

            setConfirmTotalResetModalVisible(false)
          }}
        />
      )}
    </EuiPage>
  )
}
